import { ColumnDef } from './ts-table.model';

export namespace TsTableUtils {
  /**
   * Private list of field names that should be displayed without number formatting.
   * These fields will be displayed as plain strings without any locale-specific formatting.
   * @private
   */
  const fieldsBlacklist = ['id'] as const;

  /**
   * Formats a number according to specific rules based on the field definition.
   *
   * @param rawValue - The raw value to be formatted (can be number, string, null, or undefined)
   * @param fieldDef - The column definition containing field metadata
   * @returns Formatted string representation of the number
   *
   * Rules applied:
   * 1. If the value is null, undefined or NaN, returns an empty string
   * 2. If the field name is in the blacklist (e.g. 'id'), returns the value as a plain string
   * 3. Otherwise, formats the number using US locale formatting (e.g. 1000 -> "1,000")
   */
  export const formatNumberWithFieldRules = (
    rawValue: number | string | null | undefined,
    fieldDef: ColumnDef,
  ): string => {
    const value = typeof rawValue === 'number' ? rawValue : Number(rawValue);

    if (value === null || value === undefined || Number.isNaN(value)) {
      return '';
    }

    return (fieldsBlacklist as readonly string[]).includes(fieldDef.name)
      ? value.toString()
      : value.toLocaleString('en-US');
  };

  /**
   * List of column fields that should have their header menu suppressed.
   * These are typically system columns that don't need user interaction.
   */
  const SUPPRESSED_COLUMN_FIELDS = ['checkbox', 'openDetails', 'actions'] as const;

  /**
   * Determines if a column should have its header menu suppressed.
   *
   * @param columnField - The field name of the column
   * @returns true if the column menu should be suppressed, false otherwise
   */
  export const shouldSuppressMenuItems = (columnField: string): boolean => {
    return SUPPRESSED_COLUMN_FIELDS.includes(columnField as any);
  };
}
